import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import EventCard from '../components/EventCard';
import { 
  CheckCircle, 
  Users, 
  Calendar, 
  MapPin, 
  Instagram, 
  Youtube, 
  Twitter,
  ExternalLink,
  Share2,
  UserPlus,
  UserMinus,
  Bell
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function CreatorProfile() {
  const { username } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [creator, setCreator] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);

  useEffect(() => {
    fetchCreator();
  }, [username]);

  useEffect(() => {
    if (isAuthenticated && creator) {
      checkFollowStatus();
    }
  }, [isAuthenticated, creator]);

  const fetchCreator = async () => {
    try {
      // Try by username first, then by ID
      let response;
      try {
        response = await axios.get(`${API_URL}/creators/${username}`);
      } catch {
        // If username lookup fails, try by ID
        response = await axios.get(`${API_URL}/creator/${username}`);
      }
      setCreator(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Creator not found');
    } finally {
      setLoading(false);
    }
  };

  const checkFollowStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/follow/check/${creator.id}`);
      setIsFollowing(response.data.following);
    } catch (err) {
      console.error('Failed to check follow status:', err);
    }
  };

  const handleFollow = async () => {
    if (!isAuthenticated) {
      toast.error('Please log in to follow creators');
      navigate('/login');
      return;
    }

    setFollowLoading(true);
    try {
      if (isFollowing) {
        await axios.delete(`${API_URL}/follow/${creator.id}`);
        setIsFollowing(false);
        setCreator(prev => ({
          ...prev,
          follower_count: Math.max(0, (prev.follower_count || 0) - 1)
        }));
        toast.success(`Unfollowed ${creator.display_name}`);
      } else {
        await axios.post(`${API_URL}/follow/${creator.id}`);
        setIsFollowing(true);
        setCreator(prev => ({
          ...prev,
          follower_count: (prev.follower_count || 0) + 1
        }));
        toast.success(`Now following ${creator.display_name}!`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update follow status');
    } finally {
      setFollowLoading(false);
    }
  };

  const handleShare = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url);
    toast.success('Profile link copied to clipboard!');
  };

  if (loading) {
    return (
      <div className="min-h-screen py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse space-y-6">
            <div className="flex items-center gap-6">
              <div className="w-32 h-32 bg-white/10 rounded-full" />
              <div className="flex-1 space-y-3">
                <div className="h-8 bg-white/10 rounded w-1/3" />
                <div className="h-4 bg-white/10 rounded w-1/4" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="font-heading text-2xl font-bold mb-2">Creator Not Found</h2>
          <p className="text-muted-foreground mb-4">{error}</p>
          <Link to="/events">
            <Button>Browse Events</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Profile Header */}
        <div className="bg-card border border-white/10 rounded-2xl p-8 mb-8">
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
            {/* Avatar */}
            <div className="relative">
              <div className="w-32 h-32 rounded-full bg-gradient-to-br from-primary/50 to-primary/20 flex items-center justify-center text-4xl font-heading font-bold text-white border-4 border-primary/30">
                {creator.avatar_url ? (
                  <img 
                    src={creator.avatar_url} 
                    alt={creator.display_name}
                    className="w-full h-full rounded-full object-cover"
                  />
                ) : (
                  creator.display_name?.charAt(0)?.toUpperCase()
                )}
              </div>
              {creator.verified && (
                <div className="absolute -bottom-1 -right-1 bg-primary rounded-full p-1">
                  <CheckCircle className="w-6 h-6 text-white" />
                </div>
              )}
            </div>

            {/* Info */}
            <div className="flex-1 text-center sm:text-left">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2">
                <h1 className="font-heading font-bold text-3xl">{creator.display_name}</h1>
                {creator.verified && (
                  <Badge className="bg-primary/20 text-primary border-primary/30 w-fit mx-auto sm:mx-0">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Verified
                  </Badge>
                )}
              </div>
              
              <p className="text-primary font-medium text-lg mb-3" data-testid="creator-username">
                {creator.username}
              </p>

              {creator.bio && (
                <p className="text-muted-foreground mb-4 max-w-xl">{creator.bio}</p>
              )}

              {/* Stats */}
              <div className="flex flex-wrap justify-center sm:justify-start gap-6 mb-4">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Users className="w-5 h-5" />
                  <span>{(creator.follower_count || 0).toLocaleString()} followers</span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Calendar className="w-5 h-5" />
                  <span>{creator.events?.length || 0} events</span>
                </div>
              </div>

              {/* Social Links */}
              {creator.social_handles && Object.keys(creator.social_handles).length > 0 && (
                <div className="flex flex-wrap justify-center sm:justify-start gap-3 mb-4">
                  {creator.social_handles.instagram && (
                    <a 
                      href={`https://instagram.com/${(creator.social_handles.instagram.handle || creator.social_handles.instagram).replace('@', '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-sm text-muted-foreground hover:text-pink-400 transition-colors bg-secondary/50 px-2 py-1 rounded-full"
                    >
                      <Instagram className="w-4 h-4" />
                      @{creator.social_handles.instagram.handle || creator.social_handles.instagram}
                      {creator.social_handles.instagram.verified && (
                        <CheckCircle className="w-3 h-3 text-green-400" />
                      )}
                    </a>
                  )}
                  {creator.social_handles.tiktok && (
                    <a 
                      href={`https://tiktok.com/@${(creator.social_handles.tiktok.handle || creator.social_handles.tiktok).replace('@', '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-sm text-muted-foreground hover:text-white transition-colors bg-secondary/50 px-2 py-1 rounded-full"
                    >
                      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                      </svg>
                      @{creator.social_handles.tiktok.handle || creator.social_handles.tiktok}
                      {creator.social_handles.tiktok.verified && (
                        <CheckCircle className="w-3 h-3 text-green-400" />
                      )}
                    </a>
                  )}
                  {creator.social_handles.youtube && (
                    <a 
                      href={`https://youtube.com/@${(creator.social_handles.youtube.handle || creator.social_handles.youtube).replace('@', '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-sm text-muted-foreground hover:text-red-400 transition-colors bg-secondary/50 px-2 py-1 rounded-full"
                    >
                      <Youtube className="w-4 h-4" />
                      @{creator.social_handles.youtube.handle || creator.social_handles.youtube}
                      {creator.social_handles.youtube.verified && (
                        <CheckCircle className="w-3 h-3 text-green-400" />
                      )}
                    </a>
                  )}
                  {creator.social_handles.twitter && (
                    <a 
                      href={`https://twitter.com/${(creator.social_handles.twitter.handle || creator.social_handles.twitter).replace('@', '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-sm text-muted-foreground hover:text-sky-400 transition-colors bg-secondary/50 px-2 py-1 rounded-full"
                    >
                      <Twitter className="w-4 h-4" />
                      @{creator.social_handles.twitter.handle || creator.social_handles.twitter}
                      {creator.social_handles.twitter.verified && (
                        <CheckCircle className="w-3 h-3 text-green-400" />
                      )}
                    </a>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-wrap justify-center sm:justify-start gap-3">
                {user?.id !== creator.id && (
                  <Button 
                    onClick={handleFollow} 
                    disabled={followLoading}
                    variant={isFollowing ? 'outline' : 'default'}
                    className="gap-2"
                    data-testid="follow-btn"
                  >
                    {followLoading ? (
                      <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                    ) : isFollowing ? (
                      <>
                        <UserMinus className="w-4 h-4" />
                        Unfollow
                      </>
                    ) : (
                      <>
                        <UserPlus className="w-4 h-4" />
                        Follow
                      </>
                    )}
                  </Button>
                )}
                <Button onClick={handleShare} variant="outline" className="gap-2">
                  <Share2 className="w-4 h-4" />
                  Share Profile
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Events Section */}
        <div>
          <h2 className="font-heading font-semibold text-xl mb-6">
            Events by {creator.display_name}
          </h2>

          {creator.events && creator.events.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {creator.events.map((event) => (
                <EventCard key={event.id} event={event} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-card border border-white/10 rounded-xl">
              <Calendar className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-heading font-semibold text-lg mb-2">No events yet</h3>
              <p className="text-muted-foreground">
                {creator.display_name} hasn't published any events yet. Check back soon!
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
